import React from 'react';
import { connect } from 'react-redux';
import Head from 'next/head';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash.get';

import { loadCard, fetchLatestCardsByTaxonomy } from 'redux/modules/liveBlog';
import { bulkUpdate as navUpdateAction } from 'redux/modules/navbar';
import { globalLoadComplete } from 'redux/modules/router';
import { setPageView, setVertical } from 'redux/modules/shared';

import { LiveBlogAdController } from 'lib/AdControllers';
import { layout as servicesLayoutPropType } from 'lib/CustomPropTypes/services';
import getPathName from 'lib/getPathName';
import { navbar, NAVBAR_THEME } from 'lib/navbar';

import Ad from 'components/Ad';
import AdsBundle from 'components/AdsBundle';
import { AnalyticsLaunchScripts } from 'components/AnalyticsLaunchScripts';
import Card from 'components/Card';
import CardMetadata from 'components/PageMetadata/CardMetadata';
import ScrollingAnalytics from 'components/ScrollingAnalytics';
import { HeaderAndFooter } from 'components/services/HeaderAndFooter';
import Taboola from 'components/Taboola';
import IconfontStyleTag from 'components/IconfontStyleTag';
import UniversalCheckout from 'components/UniversalCheckout';
import ErrorPage from '../_error';

import 'assets/stylesheets/main.scss';
import 'assets/stylesheets/toolkit.scss';
import globalContainerStyles from '../globalContainerStyles.module.scss';
import bodyStyles from '../../src/components/Article/ArticleBody/styles.module.scss';


const pageView = 'card';

const navbarConfig = {
  theme: () => NAVBAR_THEME.VERTICAL,
  headline: ({ content }) => get(content, 'liveBlog.card.headline.primary', null),
  activeAt: 160,
};

const mapStateToProps = ({ liveBlog }) => ({
  card: liveBlog.card,
  latestItems: liveBlog.latestItems,
});

class CardPage extends React.Component {
  static async getInitialProps(ctx) {
    const {
      req: {
        params: {
          id: rawID,
        },
      },
      res: {
        locals: {
          domain,
          hostname,
          services,
          vertical,
        },
      },
      store,
      asPath,
    } = ctx;

    const id = rawID.replace(/n(?:crd)?(\d+)/, 'ncrd$1');

    await Promise.all([
      store.dispatch(loadCard(id)),
      store.dispatch(setPageView(pageView)),
      store.dispatch(setVertical(vertical)),
    ]);

    // Populate props with HFS component data
    const layout = {};
    if (services && services.layout && services.layout.getComponent) {
      layout.header = await services.layout.getComponent(vertical, 'header');
      layout.footer = await services.layout.getComponent(vertical, 'footer');
    }

    const { liveBlog: { card, error } } = store.getState();

    const statusCode = error ? error.status : 200;
    if (statusCode >= 400) {
      ctx.res.statusCode = statusCode;
    }

    const primaryUrl = get(card, 'url.primary', '');
    const headline = get(card, 'headline', {});
    const path = get(card, 'taxonomy.primaryChannel.path', false);

    const secondaryPromiseArray = [
      store.dispatch(navUpdateAction({
        shareUrl: getPathName(primaryUrl),
        headline,
      })),

      store.dispatch(globalLoadComplete({
        domain,
        host: hostname,
        navigating: false,
        path: asPath,
        transporting: false,
      })),
    ];

    if (path) {
      const params = {
        path,
        page: 1,
        queryLimit: 11,
      };
      // Load recent cards of same taxonomy
      secondaryPromiseArray.push(
        store.dispatch(fetchLatestCardsByTaxonomy(params)),
      );
    }

    await Promise.all(secondaryPromiseArray);

    return {
      layout,
      pageView,
      path: asPath,
      vertical,
      statusCode,
    };
  }

  static propTypes = {
    card: PropTypes.objectOf(PropTypes.any).isRequired,
    latestItems: PropTypes.arrayOf(
      PropTypes.shape({}),
    ),
    layout: servicesLayoutPropType.isRequired,
    vertical: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    themesCssPath: PropTypes.string,
    statusCode: PropTypes.number,
  };

  static childContextTypes = {
    isLiveBlog: PropTypes.bool,
  };

  static defaultProps = {
    latestItems: [],
    themesCssPath: '',
    statusCode: 200,
  };

  getChildContext = () => ({
    isLiveBlog: true,
  })

  registerAd(el) {
    const ad = el && el.querySelector(`.${bodyStyles.rightRailAd}`);
    if (ad) {
      this.ads = [...this.ads, ad];
    }
  }

  render() {
    const {
      card, latestItems, layout, path, themesCssPath, vertical, statusCode,
    } = this.props;

    if (!card) {
      return (
        <HeaderAndFooter layout={layout}>
          <ErrorPage statusCode={statusCode === 404 ? 404 : 500} vertical={vertical} />
        </HeaderAndFooter>
      );
    }

    const withoutCanonical = latestItems.filter((item) => item.id !== card.id);
    const cardsWithAds = LiveBlogAdController.insertBoxinlineAds(withoutCanonical);

    return (
      <>
        <Head>
          {themesCssPath && (
            <link href={themesCssPath} rel="stylesheet" key={themesCssPath} />
          )}
          <IconfontStyleTag />
        </Head>

        <div
          className={classNames(
            globalContainerStyles.container,
            'bg-knockout-primary',
          )}
          id="content"
        >
          <HeaderAndFooter layout={layout}>
            <ScrollingAnalytics contentType="liveBlog" contentUrl={card.url.primary}>
              <CardMetadata card={card} vertical={vertical} />

              <div className="layout-grid-container mv8">
                <div className={classNames(bodyStyles.body, bodyStyles.hoverLinks, 'publico-txt f4 f5-m lh-copy gray-100')}>
                  <Card {...card} path={path} />

                  {cardsWithAds.map((cardData) => {
                    if (cardData.type === 'ad') {
                      return (
                        <Ad
                          slot="boxinline"
                          key={cardData.key}
                          offsetViewport={10}
                        />
                      );
                    }
                    return (
                      <Card {...cardData} key={cardData.id} path={path} />
                    );
                  })}
                </div>

                <div className={bodyStyles.rightRail}>
                  <Taboola
                    id={card.id}
                    canonicalUrl={card.url.primary}
                    ecommerceEnabled={false}
                    isRail
                  />
                  <Ad
                    slot="boxrail"
                    adClass={bodyStyles.rightRailAd}
                    offset={10}
                  />
                </div>
                <Taboola
                  id={card.id}
                  canonicalUrl={card.url.primary}
                  ecommerceEnabled={false}
                />
              </div>
            </ScrollingAnalytics>
          </HeaderAndFooter>
        </div>

        <AnalyticsLaunchScripts />
        <AdsBundle />
        <UniversalCheckout vertical={vertical} />
      </>
    );
  }
}

export default navbar(navbarConfig)(
  connect(mapStateToProps)(
    CardPage,
  ),
);
